/**
 * The pure half of installing and updating the Snapshot (issue #3): the manifest's content
 * hash, the files a Snapshot ships, the download plan against what a cache already holds, and
 * the launch gate that decides whether a staged update applies now. Nothing here touches the
 * network or a cache.
 */
import type { FileRef, Manifest } from "../../shared/snapshot-format";
import type { Installed, PwaState } from "./state";

/** One file a Snapshot ships, with the group the install screen reports it under. */
export interface DataFile extends FileRef {
  /** The Pack's label, or "Catalogue" for the index, search, redirects and config files. */
  group: string;
}

export const CATALOGUE_GROUP = "Catalogue";

export async function sha256Hex(data: ArrayBuffer | Uint8Array): Promise<string> {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const digest = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** The content hash of `manifest.json`: the first 16 hex digits of its SHA-256, the Snapshot cache's name. */
export async function manifestHash(text: string): Promise<string> {
  return (await sha256Hex(new TextEncoder().encode(text))).slice(0, 16);
}

/** Every file a Snapshot ships besides the manifest itself, catalogue files first. */
export function snapshotFiles(manifest: Manifest): DataFile[] {
  const files: DataFile[] = [
    { path: manifest.index.path, bytes: manifest.index.bytes, sha256: manifest.index.sha256, group: CATALOGUE_GROUP },
    { path: manifest.search.path, bytes: manifest.search.bytes, sha256: manifest.search.sha256, group: CATALOGUE_GROUP },
    { path: manifest.redirects.path, bytes: manifest.redirects.bytes, sha256: manifest.redirects.sha256, group: CATALOGUE_GROUP },
    { path: manifest.config.path, bytes: manifest.config.bytes, sha256: manifest.config.sha256, group: CATALOGUE_GROUP },
  ];
  for (const pack of manifest.packs) {
    for (const shard of pack.shards) files.push({ path: shard.path, bytes: shard.bytes, sha256: shard.sha256, group: pack.label });
  }
  return files;
}

/** The URL a download fetches: the content hash as a query keeps the worker and any HTTP cache out of the way. */
export function downloadUrl(file: FileRef): string {
  return `/data/${file.path}?sha=${file.sha256.slice(0, 12)}`;
}

/** The URL a file is cached under, the one the app reads. */
export function dataUrl(path: string): string {
  return `/data/${path}`;
}

/** A file another Snapshot cache already holds with the same content. */
export interface CopySource {
  cache: string;
  path: string;
}

export interface DownloadPlan {
  /** Already in the target cache. */
  present: DataFile[];
  /** Same content in another Snapshot cache: copied, not fetched. */
  copy: { file: DataFile; from: CopySource }[];
  /** Fetched from the network. */
  fetch: DataFile[];
}

/**
 * What a download has to do for `manifest`: skip what `present` holds (paths in the target
 * cache), copy what another cache holds under the same path with the same hash, fetch the rest.
 */
export function planDownload(manifest: Manifest, present: ReadonlySet<string>, others: readonly { cache: string; manifest: Manifest }[] = []): DownloadPlan {
  const plan: DownloadPlan = { present: [], copy: [], fetch: [] };
  const sources = new Map<string, CopySource>();
  for (const other of others) {
    for (const f of snapshotFiles(other.manifest)) {
      const key = `${f.path}#${f.sha256}`;
      if (!sources.has(key)) sources.set(key, { cache: other.cache, path: f.path });
    }
  }
  for (const file of snapshotFiles(manifest)) {
    if (present.has(file.path)) plan.present.push(file);
    else {
      const from = sources.get(`${file.path}#${file.sha256}`);
      if (from) plan.copy.push({ file, from });
      else plan.fetch.push(file);
    }
  }
  return plan;
}

/** Progress of one download, per group and in total, for the install screen. */
export interface DownloadProgress {
  groups: { label: string; bytes: number; done: number; files: number; filesDone: number }[];
  bytes: number;
  done: number;
  files: number;
  filesDone: number;
  /** The file in flight last reported, for the screen's detail line. */
  current: string | null;
}

export function initialProgress(files: readonly DataFile[], done: ReadonlySet<string>): DownloadProgress {
  const groups = new Map<string, DownloadProgress["groups"][number]>();
  const p: DownloadProgress = { groups: [], bytes: 0, done: 0, files: files.length, filesDone: 0, current: null };
  for (const f of files) {
    let g = groups.get(f.group);
    if (!g) {
      g = { label: f.group, bytes: 0, done: 0, files: 0, filesDone: 0 };
      groups.set(f.group, g);
      p.groups.push(g);
    }
    g.bytes += f.bytes;
    g.files += 1;
    p.bytes += f.bytes;
    if (done.has(f.path)) {
      g.done += f.bytes;
      g.filesDone += 1;
      p.done += f.bytes;
      p.filesDone += 1;
    }
  }
  return p;
}

/** A new progress with `file` counted done (the input is left as it is). */
export function advanceProgress(p: DownloadProgress, file: DataFile): DownloadProgress {
  return {
    ...p,
    groups: p.groups.map((g) => (g.label === file.group ? { ...g, done: g.done + file.bytes, filesDone: g.filesDone + 1 } : g)),
    done: p.done + file.bytes,
    filesDone: p.filesDone + 1,
    current: file.path,
  };
}

/** What the network serves now, read from `/version.json` and the fresh manifest. */
export interface Deployed {
  build: string;
  data: string;
}

export type Difference = "same" | "shell" | "data" | "both";

/** How a deployed build differs from an installed pair. */
export function compareDeployed(installed: Installed, deployed: Deployed): Difference {
  const shell = installed.build !== deployed.build;
  const data = installed.data !== deployed.data;
  return shell && data ? "both" : shell ? "shell" : data ? "data" : "same";
}

export type LaunchDecision =
  /** No Snapshot installed yet: the install screen, nothing else, until it is. */
  | { action: "install"; resume: string | null }
  /** A staged update applies now, before the UI loads. */
  | { action: "apply"; from: Installed; to: Installed }
  /** The app runs on what is active; `pending` names a staged update that could not apply and why. */
  | { action: "run"; active: Installed; pending: Installed | null; reason: string | null };

/**
 * The launch gate (docs/design.md, "Delivery"): a staged update applies on a launch only, only
 * with no Session open, and only when its shell finished caching; otherwise it waits for the
 * next launch. `sessionsOpen` names the Characters in play.
 */
export function decideLaunch(state: PwaState, sessionsOpen: readonly string[], shellReady: boolean): LaunchDecision {
  if (!state.active) return { action: "install", resume: state.installing };
  if (!state.staged) return { action: "run", active: state.active, pending: null, reason: null };
  if (sessionsOpen.length > 0) {
    const list = sessionsOpen.join(", ");
    return { action: "run", active: state.active, pending: state.staged, reason: `${list} ${sessionsOpen.length === 1 ? "has" : "have"} a Session open; the update waits for a launch with every Session closed.` };
  }
  if (!shellReady) return { action: "run", active: state.active, pending: state.staged, reason: "The new app files have not finished downloading; the update waits for the next launch." };
  return { action: "apply", from: state.active, to: state.staged };
}

export function formatBytes(n: number): string {
  if (n < 1e6) return `${(n / 1e3).toFixed(0)} kB`;
  return `${(n / 1e6).toFixed(1)} MB`;
}
